import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { cn } from '../../lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Clock, AlertTriangle, CalendarIcon, ChevronsUpDown, Check } from 'lucide-react';
import { calcEndTime } from '../../utils/calendarHelpers';
import { DURATION_OPTIONS, MOTIVOS_PRESET, MODALIDAD_OPTIONS } from '../../utils/calendarConstants';

/**
 * Diálogo "Nueva Cita" del calendario (incluye su botón disparador).
 * Es un componente controlado: todo el estado vive en CalendarPage y se pasa
 * por props para mantener el comportamiento idéntico al original.
 */
export const NewAppointmentDialog = ({
  open, onOpenChange,
  newApt, setNewApt,
  patients, filteredPatients, doctors, isDoctor, selectedDoctor,
  timeSlots, occupied, conflict, slotDuration,
  quickPatientOpen, setQuickPatientOpen,
  quickPatientData, setQuickPatientData, quickPatientSaving, onQuickPatientCreate,
  patientComboOpen, setPatientComboOpen, patientSearch, setPatientSearch,
  calendarOpen, setCalendarOpen,
  onCreate,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="create-appointment-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Cita
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Agendar Nueva Cita</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">

          {/* Paciente — combobox con búsqueda + creación rápida */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Paciente *</Label>
              <button
                type="button"
                onClick={() => { setQuickPatientOpen(v => !v); setQuickPatientData({ nombre: '', apellido: '', telefono: '' }); }}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="h-3 w-3" />
                {quickPatientOpen ? 'Cancelar' : 'Nuevo paciente'}
              </button>
            </div>

            {/* Mini-form creación rápida */}
            {quickPatientOpen && (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-2">
                <p className="text-xs text-muted-foreground">Completa el perfil completo después en Pacientes</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Nombre *"
                    value={quickPatientData.nombre}
                    onChange={e => setQuickPatientData(p => ({ ...p, nombre: e.target.value }))}
                    className="h-8 text-sm"
                  />
                  <Input
                    placeholder="Apellido *"
                    value={quickPatientData.apellido}
                    onChange={e => setQuickPatientData(p => ({ ...p, apellido: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Teléfono"
                    value={quickPatientData.telefono}
                    onChange={e => setQuickPatientData(p => ({ ...p, telefono: e.target.value }))}
                    className="h-8 text-sm flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={onQuickPatientCreate}
                    disabled={quickPatientSaving}
                    className="h-8"
                  >
                    {quickPatientSaving ? '…' : 'Crear'}
                  </Button>
                </div>
              </div>
            )}

            <Popover open={patientComboOpen} onOpenChange={setPatientComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  data-testid="apt-patient-select"
                  className="w-full justify-between font-normal"
                >
                  {newApt.paciente_id
                    ? (() => { const p = patients.find(x => x.id === newApt.paciente_id); return p ? `${p.nombre} ${p.apellido}` : 'Seleccionar paciente'; })()
                    : <span className="text-muted-foreground">Buscar paciente…</span>
                  }
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Nombre o teléfono…"
                    value={patientSearch}
                    onValueChange={setPatientSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No se encontraron pacientes.</CommandEmpty>
                    <CommandGroup>
                      {filteredPatients.map(p => (
                        <CommandItem
                          key={p.id}
                          value={p.id}
                          onSelect={() => {
                            setNewApt({ ...newApt, paciente_id: p.id });
                            setPatientComboOpen(false);
                            setPatientSearch('');
                          }}
                        >
                          <Check className={cn('mr-2 h-4 w-4 flex-shrink-0', newApt.paciente_id === p.id ? 'opacity-100' : 'opacity-0')} />
                          <div className="min-w-0">
                            <div className="font-medium truncate">{p.nombre} {p.apellido}</div>
                            <div className="text-xs text-muted-foreground">{p.telefono}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Doctor */}
          {!isDoctor ? (
          <div className="space-y-2">
            <Label>Doctor *</Label>
            <Select value={newApt.doctor_id} onValueChange={v => setNewApt({ ...newApt, doctor_id: v })}>
              <SelectTrigger data-testid="apt-doctor-select">
                <SelectValue placeholder="Seleccionar doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                      {d.nombre}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          ) : (
          <div className="space-y-2">
            <Label>Doctor</Label>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border/50 bg-muted/30 text-sm">
              {(() => { const d = doctors.find(x => x.id === selectedDoctor); return d ? (<><div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />{d.nombre}</>) : 'Sin perfil asignado'; })()}
            </div>
            {/* hidden input para mantener el valor */}
            <input type="hidden" value={selectedDoctor} onChange={() => {}} />
          </div>
          )}

          {/* Fecha */}
          <div className="space-y-2">
            <Label>Fecha *</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn('w-full justify-start text-left font-normal', !newApt.fecha && 'text-muted-foreground')}
                  data-testid="apt-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {newApt.fecha ? format(new Date(newApt.fecha + 'T00:00:00'), 'PPP', { locale: es }) : 'Seleccionar fecha'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={newApt.fecha ? new Date(newApt.fecha + 'T00:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) { setNewApt({ ...newApt, fecha: format(date, 'yyyy-MM-dd') }); setCalendarOpen(false); }
                  }}
                  locale={es}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Hora inicio + Duración */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hora Inicio *</Label>
              <Select value={newApt.hora_inicio} onValueChange={v => setNewApt({ ...newApt, hora_inicio: v })}>
                <SelectTrigger data-testid="apt-start-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(slot => (
                    <SelectItem key={slot} value={slot} disabled={occupied.has(slot)}>
                      {slot}{occupied.has(slot) ? ' · ocupado' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duración *</Label>
              <Select value={newApt.duracion.toString()} onValueChange={v => setNewApt({ ...newApt, duracion: parseInt(v) })}>
                <SelectTrigger data-testid="apt-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                      {opt.value === slotDuration && (
                        <span className="ml-2 text-[10px] text-muted-foreground">(default)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Modalidad */}
          <div className="space-y-2">
            <Label>Modalidad *</Label>
            <Select value={newApt.modalidad} onValueChange={v => setNewApt({ ...newApt, modalidad: v })}>
              <SelectTrigger data-testid="apt-modalidad-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODALIDAD_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label} · ${opt.costo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preview hora fin */}
          <div className="flex items-center gap-2 text-sm bg-muted/50 px-3 py-2 rounded-lg">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Hora fin:</span>
            <span className="font-semibold">{calcEndTime(newApt.hora_inicio, newApt.duracion)}</span>
            <span className="text-muted-foreground ml-auto">{newApt.duracion} min</span>
          </div>

          {/* Alerta de conflicto */}
          {conflict && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800 dark:text-amber-300">Horario ocupado</p>
                <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
                  {conflict.paciente_nombre} ya tiene cita de {conflict.hora_inicio}–{conflict.hora_fin}
                </p>
              </div>
            </div>
          )}

          {/* Motivo */}
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <div className="flex flex-wrap gap-1.5">
              {MOTIVOS_PRESET.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setNewApt({ ...newApt, motivo: m })}
                  className={cn(
                    'text-xs px-2 py-1 rounded-full border transition-colors',
                    newApt.motivo === m
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'border-border/50 hover:border-border text-muted-foreground hover:text-foreground'
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
            <Input
              placeholder="Ej: Limpieza, revisión, extracción..."
              value={newApt.motivo}
              onChange={(e) => setNewApt({ ...newApt, motivo: e.target.value })}
              data-testid="apt-motivo"
            />
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas (opcional)</Label>
            <Textarea
              placeholder="Notas adicionales..."
              value={newApt.notas}
              onChange={(e) => setNewApt({ ...newApt, notas: e.target.value })}
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              className="flex-1"
              onClick={onCreate}
              disabled={!!conflict}
              data-testid="save-appointment-btn"
            >
              Agendar Cita
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
